import apiRoutes from '../constants/apiRoutes';
import baseApi from '../services/api';
import { ApiResponse } from './types/common';
import { BasicCompanyDetails } from './types/pvxPartners';

export enum SubmoduleScopeType {
  GLOBAL = 'GLOBAL',
  RESTRICTED = 'RESTRICTED',
  COMPANY = 'COMPANY',
}
export interface UserSubmoduleMap {
  membershipId?: string | null;
  moduleId?: string;
  submoduleId: string;
  filterIds: string[];
  accessType?: string | null;
  scopeType?: SubmoduleScopeType | null;
  hidden?: boolean;
}

export interface UserPayload {
  id?: string;
  email: string;
  userRole: string;
  username: string;
  companyId: string;
  submoduleList: UserSubmoduleMap[];
  /** Omit or set to undefined to default to CUSTOM on the backend. */
  internalUserAccessTemplateType?: string;
}

export interface AccessTemplateSubmodule {
  submoduleId: string;
  submoduleName: string;
}

export interface AccessTemplateType {
  internalUserAccessTemplateType: string;
  label: string;
  submodules: AccessTemplateSubmodule[];
}

export interface AccessTemplateTypeListItem {
  internalUserAccessTemplateType: string;
  label: string;
}

export interface CompanyUser {
  id: string;
  companyId: string;
  email: string;
  username: string;
  status: string;
  role: string;
  company: string;
  invitationType: string;
  preference?: {
    language: string;
  };
}

interface EditCompanyPayload {
  id: string;
  name: string;
  address?: string;
  companyHQ?: string;
}

export interface CompanyDetailsDto {
  companyId: string; // uuid
  clientCode: string;
  name: string;
  address: string | null;
  companyHQ: string | null;
  pipedriveDealId: number | null;
  pvxPocUser: string | null;
  secondaryPvxPocs: string[];
  financialYearEndDate: string | null;
  consolidatedEntities: string[];
  dealSource: string | null;
}

// Full replace: every field is sent, and an omitted or null one is cleared on all the company's games
export interface UpdateCompanyDetailsPayload {
  pipedriveDealId: number | null;
  pvxPocUser: string | null;
  secondaryPvxPocs: string[];
  financialYearEndDate: string | null; // 'MM-DD'
  consolidatedEntities: string[];
  dealSource: string | null;
}

const company = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<any, any>({
      query: () => `${apiRoutes.companies}?sort=name&sortOrder=ASC`,
    }),

    getCompanyDetails: builder.query<ApiResponse<CompanyDetailsDto>, string>({
      query: (clientCode) => apiRoutes.companyDetails(clientCode),
    }),

    updateCompanyDetails: builder.mutation<
      ApiResponse<CompanyDetailsDto>,
      { clientCode: string; body: UpdateCompanyDetailsPayload }
    >({
      query: ({ clientCode, body }) => ({
        url: apiRoutes.companyDetails(clientCode),
        method: 'PUT',
        body,
      }),
    }),

    createCompany: builder.mutation<any, any>({
      query: ({
        name,
        address,
        companyHQ,
      }: {
        name: string;
        address?: string;
        companyHQ?: string;
      }) => ({
        url: `${apiRoutes.companies}`,
        method: 'POST',
        body: { name, address, companyHQ },
      }),
      invalidatesTags: ['companyList'],
    }),
    editCompany: builder.mutation<any, EditCompanyPayload>({
      query: ({ id, ...body }) => ({
        url: `${apiRoutes.companies}/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['companyList'],
    }),
    basicCompanyDetails: builder.query<
      ApiResponse<BasicCompanyDetails[]>,
      {
        status?: string;
        page?: number;
        size?: number;
        sortBy?: string;
        sortOrder?: string;
        companyType?: string;
        paginated?: boolean;
      }
    >({
      query: ({ status, page, size, sortBy, sortOrder, companyType, paginated }) => {
        const queryParams = new URLSearchParams();

        if (paginated === false) {
          queryParams.set('paginated', 'false');
        }

        if (status) {
          queryParams.set('status', status);
        }

        if (companyType) {
          queryParams.set('companyType', companyType);
        }

        if (paginated !== false) {
          if (page !== undefined && page !== null) {
            queryParams.set('pageNumber', String(page));
          }

          if (size !== undefined && size !== null) {
            queryParams.set('pageSize', String(size));
          }
        }

        if (sortBy) {
          queryParams.set('sort', sortBy);
        }

        if (sortOrder) {
          queryParams.set('sortOrder', sortOrder);
        }

        const queryString = queryParams.toString();
        return `${apiRoutes.companiesBasicDetails}${queryString ? `?${queryString}` : ''}`;
      },
    }),
    getUsersByCompany: builder.query<ApiResponse<CompanyUser[]>, string>({
      query: (companyId) => apiRoutes.companyUsers(companyId),
    }),
  }),
});

export const {
  useLazyGetCompaniesQuery,
  useCreateCompanyMutation,
  useEditCompanyMutation,
  useBasicCompanyDetailsQuery,
  useLazyGetUsersByCompanyQuery,
  useLazyGetCompanyDetailsQuery,
  useUpdateCompanyDetailsMutation,
} = company;
